import { ColumnDef, SortingState, TableMeta, VisibilityState } from '@tanstack/react-table'
import { ReactElement, ReactNode } from 'react'
import { TableLayoutComponent } from './TableLayoutComponent'
import { useTableLayout } from './useTableLayout'
import type { BulkActionsConfig } from './bulkActions/types'
import type { FixedRules } from 'app/types/filters'
import { FilterableColumn, FilterObject } from 'app/filter.utils'

type TableLayoutProps<T> = {
  data: { hits: T[]; total: number } | undefined
  columns: ColumnDef<T, any>[]
  filterableColumns?: FilterableColumn[]
  fixedRules?: FixedRules
  defaultJoin?: `and` | `or`
  meta?: TableMeta<T>
  /** Appends the next page onto `data`; without it the table stays on its first page. */
  loadMore?: () => Promise<unknown>
  extraButtons?: (columns: ColumnDef<T, any>[], columnVisibility: VisibilityState) => ReactElement[]
  /**
   * The very end of the toolbar row, after bulk actions, export and the column controls: a list
   * page's view switch, where its board draws them too (see ListBoardHeader).
   */
  trailingControls?: ReactNode
  /**
   * A list page's Settings carries column lines and the column editor, so the toolbar leaves
   * their two buttons out. The editor itself stays mounted for Settings to open.
   */
  viewToolsInSettings?: boolean
  hideControls?: boolean
  drawer?: ReactElement | null
  initialSort?: SortingState
  initialFilters?: FilterObject[]
  edgePadded?: boolean
  /** When provided, enables row-selection mode and the bulk-action bar. */
  bulkActions?: BulkActionsConfig<T>
  /** Shown inside the table frame when there are no rows and nothing is filtered. */
  emptyState?: ReactNode
  /** For pages whose own filters live outside the URL; see TableLayoutComponent. */
  narrowed?: boolean
  /** See useTableLayout. */
  defaultColumnWeights?: Record<string, number>
}

export default function TableLayout<T>(props: TableLayoutProps<T>) {
  const hookData = useTableLayout(props)
  return (
    <TableLayoutComponent
      {...hookData}
      edgePadded={props.edgePadded}
      trailingControls={props.trailingControls}
      viewToolsInSettings={props.viewToolsInSettings}
      emptyState={props.emptyState}
      narrowed={props.narrowed}
      fixedRules={props.fixedRules}
    />
  )
}
